import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import { IcUserBanIcon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IPopoverMenuItem } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { showAlert } from 'APP/Tasks/app/app';
import { Avatar, AvatarTypes } from 'APP/components/Avatar/Avatar';
import { useUserProfile } from 'APP/main/hooks/userProfile/useUserProfile';
import { ServerErrorCode } from 'APP/restApi';
import { GroupServerErrorCode } from 'APP/restApi/constants/group.errors';
import type { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';

const knownError: Record<string, string> = {
  [ServerErrorCode.HasNoPermissionsToPerformAction]: 'no_permission_alert',
  [ServerErrorCode.HasNoPermissionToRemoveSpaceOwner]: 'space_admin_kick_owner_alert',
};

interface ISenderPopoverMenuPresenterParams {
  message: ChatMessage;
  senderId: string;
}

interface ISenderPopoverMenuPresenter {
  onShowProfile(): void;
  banParticipant(): void;
  menuItems: IPopoverMenuItem[];
}

export const useSenderPopoverMenuForAdminPresenter = ({
  message,
  senderId,
}: ISenderPopoverMenuPresenterParams): ISenderPopoverMenuPresenter => {
  const { t } = useTranslation();
  const source = useAsObservableSource({ message, senderId });
  const userProfile = useUserProfile(senderId, { groupId: message.groupId, messageId: message.id });

  const presenter = useLocalStore<ISenderPopoverMenuPresenter>(() => ({
    get menuItems(): IPopoverMenuItem[] {
      return [
        {
          key: 'user-profile',
          title: source.message.avatarTitle,
          onClick: presenter.onShowProfile,
          customIcon: (
            <Avatar
              type={AvatarTypes.Round_0}
              url={source.message.avatarUrl}
              title={source.message.avatarTitle}
              backgroundColorIndex={source.message.avatarColorIndex || '0'}
            />
          ),
          isVisible: true,
        },
        {
          key: 'ban',
          title: t('ban_user'),
          onClick: presenter.banParticipant,
          icon: IcUserBanIcon,
          isVisible: true,
        },
      ].filter((item) => item.isVisible);
    },

    onShowProfile(): void {
      userProfile.show();
    },

    async banParticipant(): Promise<void> {
      const { isConfirmed, isChecked: deleteAllMessages } = await Tasks.app.showConfirmWithCheckbox(
        {
          text: t('ban_user_alert', { 0: source.message.avatarTitle }),
          checkBoxText: t('checkbox_delete_messages'),
        }
      );

      if (!isConfirmed) {
        return;
      }

      const { error } = await Tasks.group.banParticipants(
        source.message.groupId,
        [source.senderId],
        deleteAllMessages
      );

      if (error) {
        if (error === GroupServerErrorCode.UserIsBannedInTheGroup) {
          return;
        }

        showAlert(t(knownError[error] || 'something_went_wrong'));
        return;
      }

      deleteAllMessages
        ? Tasks.app.showToast(t('delete_banned_user_messages_snackbar'))
        : Tasks.app.showToast(
            t('common_user_banned_snackbar', {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore TODO: fix typings
              0: source.message.senderUser?.displayName || '',
            })
          );
    },
  }));

  return presenter;
};
